import React from "react";
import { CharacterDiceTemplate, CharacterDiceTemplateRequest } from "../../../types/types";
import { Button, Input, TextArea } from "../../ui";
import { useFormValidation } from "../../../hooks";
import { commonValidationRules } from "../../../utils/validation";

export interface DiceTemplateFormProps {
  template?: CharacterDiceTemplate;
  isEditing?: boolean;
  onSave: (_templateData: CharacterDiceTemplateRequest) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  className?: string;
}

const DiceTemplateForm: React.FC<DiceTemplateFormProps> = ({
  template,
  isEditing = false,
  onSave,
  onCancel,
  loading = false,
  className = "",
}) => {
  const form = useFormValidation<CharacterDiceTemplateRequest>({
    initialValues: {
      name: template?.name || "",
      diceNotation: template?.diceNotation || "",
      modifier: template?.modifier || 0,
      description: template?.description || "",
      skillType: template?.skillType || "",
    },
    validationRules: {
      name: commonValidationRules.name,
      diceNotation: commonValidationRules.required,
      modifier: {
        custom: (value: unknown) => {
          const num = Number(value);
          if (isNaN(num)) return "Modifier must be a number";
          return null;
        },
      },
      description: commonValidationRules.description,
      skillType: commonValidationRules.required,
    },
    onSubmit: async (values) => {
      await onSave(values);
    },
  });

  const handleCancel = () => {
    form.resetForm();
    onCancel();
  };

  return (
    <div className={`bg-gray-800 border border-gray-700 rounded-lg p-6 ${className}`}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-white">
          {isEditing ? "Edit Fate Formula" : "Create Fate Formula"}
        </h3>
      </div>

      <form onSubmit={form.handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Fate Formula Name"
            name="name"
            value={form.values.name}
            onChange={form.handleChange("name")}
            onBlur={form.handleBlur("name")}
            error={form.errors.name}
            placeholder="e.g., Attack Roll, Skill Check"
            required
            fullWidth
          />

          <Input
            label="Dice Notation"
            name="diceNotation"
            value={form.values.diceNotation}
            onChange={form.handleChange("diceNotation")}
            onBlur={form.handleBlur("diceNotation")}
            error={form.errors.diceNotation}
            placeholder="e.g., 1d20, 2d6+3"
            required
            fullWidth
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Modifier"
            name="modifier"
            type="number"
            value={form.values.modifier.toString()}
            onChange={form.handleChange("modifier")}
            onBlur={form.handleBlur("modifier")}
            error={form.errors.modifier}
            placeholder="0"
            fullWidth
          />

          <Input
            label="Category"
            name="skillType"
            value={form.values.skillType}
            onChange={form.handleChange("skillType")}
            onBlur={form.handleBlur("skillType")}
            error={form.errors.skillType}
            placeholder="e.g., Combat, Healing, Saving Throw"
            required
            fullWidth
          />
        </div>

        <TextArea
          label="Description"
          name="description"
          value={form.values.description}
          onChange={form.handleChange("description")}
          onBlur={form.handleBlur("description")}
          error={form.errors.description}
          placeholder="Describe when and how to use this fate formula..."
          rows={3}
          fullWidth
        />

        <div className="flex justify-end space-x-3 pt-4">
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading || form.isSubmitting}
          >
            {isEditing ? "Update Fate Formula" : "Create Fate Formula"}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DiceTemplateForm;
